import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { UserContext } from './user.context';
import socketIOClient from "socket.io-client";

export const PrinterContext = createContext();

export const usePrinter = () => {
  return useContext(PrinterContext);
};

export const PrinterProvider = ({ children }) => {
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [errorOccurred, setErrorOccurred] = useState(false);
  const [isDisablePrinter, setIsDisablePrinter] = useState(false);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;
  const wsRef = useRef(null);
  const { user } = useContext(UserContext);
  const [socket, setSocket] = useState(null);
  const [socketError, setSocketError] = useState(false);

  // Gestione connessione Socket.io real-time
  useEffect(() => {
    if (user && user.id) {
      const s = socketIOClient(process.env.REACT_APP_SERVER_URL, {
        secure: true,
        transports: ["websocket"],
        query: { ownerId: user.id },
      });
      setSocket(s);
    }
  }, [user]);

  // Gestione eventi in tempo reale da Socket.io
  useEffect(() => {
    if (socket) {
      const orderPrintedListener = (data) => {
        if (!data || !data.type) return;

        const { type, id, filter } = data;

        if (type === 'TABLE') {
          dash_sendOrderToPrinter_table(id, 0, filter);
        } else if (type === 'R_C') {
          console.log('sono ritiro consegna');
          dash_sendOrderToPrinter_R_C(id, 0, filter);
        } else {
          console.warn('Tipo di ordine sconosciuto:', type);
        }
      };

      socket.on("ORDER_PRINTED_NOTIFICATION", orderPrintedListener);

      return () => {
        socket.off("ORDER_PRINTED_NOTIFICATION", orderPrintedListener);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  // Gestione WebSocket locale con la stampante dell'Owner
  useEffect(() => {
    if (!user?.ownerIp) {
      setErrorOccurred(true);
      return;
    }

    let reconnectTimeout = null;

    const connectWebSocket = () => {
      if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        console.error('WebSocket: troppi tentativi di riconnessione, interrotto.');
        setErrorOccurred(true);
        return;
      }

      console.log(`Tentativo di connessione WebSocket (${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})...`);

      try {
        wsRef.current = new WebSocket(`wss://${user.ownerIp}:3001`);

        wsRef.current.onopen = () => {
          console.log('Connesso al server WebSocket per la stampa!');
          setConnectionStatus('Connected');
          reconnectAttemptsRef.current = 0;
          setErrorOccurred(false);
        };

        wsRef.current.onerror = (error) => {
          console.error('Errore nella connessione WebSocket:', error);
          setConnectionStatus('Error');
          setErrorOccurred(true);
        };

        wsRef.current.onclose = () => {
          console.log('Connessione WebSocket chiusa');
          setConnectionStatus('Disconnected');

          reconnectAttemptsRef.current += 1;

          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectTimeout = setTimeout(() => {
              console.log('Tentativo di riconnessione...');
              connectWebSocket();
            }, 5000);
          } else {
            setErrorOccurred(true);
          }
        };

        wsRef.current.onmessage = (event) => {
          console.log('Messaggio ricevuto:', event.data);
          setIsDisablePrinter(false);
        };
      } catch (error) {
        console.error('Errore imprevisto durante la connessione WebSocket:', error);
        setErrorOccurred(true);
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.close();
      }
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, [user?.ownerIp]);

  // Funzione master di invio (chiamata dai vecchi metodi basati su DB)
  const sendOrderToPrinter = async (endpoint, payload, sconto, messageType, filter) => {
    try {
      setIsDisablePrinter(true);
      const res = await axios.post(`/api/printer/${endpoint}`, { ...payload, filter });
      if (res.data.success) {
        console.log('Dati ottenuti dal server:', res.data);

        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          const message = {
            type: messageType,
            data: res.data.data,
            sconto: sconto
          };
          wsRef.current.send(JSON.stringify(message));
          console.log('Ordine inviato alla stampante:', message);
        } else {
          console.error('WebSocket non connesso');
          setSocketError(true);
        }
      } else {
        console.error('Errore nel recupero dei dati:', res.data.error);
      }
    } catch (error) {
      console.error(
        'Errore nella richiesta al server:',
        error.response ? error.response.data : error.message
      );
    } finally {
      setIsDisablePrinter(false);
    }
  };

  /**
   * OPZIONE A: Nuova funzione per stampare SOLO il carrello corrente appena inviato.
   * Evita di interrogare lo storico del database prevenendo doppie stampe.
   */
  const printCurrentCartOnly = async (tableId, currentCart) => {
    const filteredCart = currentCart.filter(({ amount }) => amount > 0);
    if (filteredCart.length === 0) return;

    try {
      setIsDisablePrinter(true);
      // Chiama il nuovo endpoint backend che formatta l'array ricevuto senza fare SELECT dei vecchi piatti
      const res = await axios.post('/api/printer/current_cart', { tableId, cart: filteredCart });

      if (res.data.success) {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          const message = {
            type: 'PRINT_ORDER_TABLE', // Manteniamo lo stesso TYPE così il programmino locale lo processa normalmente
            data: res.data.data,
            sconto: 0
          };
          wsRef.current.send(JSON.stringify(message));
          console.log('Carrello corrente inviato al server di stampa:', message);
        } else {
          console.error('WebSocket non connesso');
          setSocketError(true);
        }
      } else {
        console.error('Errore nel recupero dei dati di stampa carrello:', res.data.error);
      }
    } catch (error) {
      console.error('Errore nella richiesta di stampa del carrello corrente:', error.message);
    } finally {
      setIsDisablePrinter(false);
    }
  };

  // Metodi legacy mantenuti intatti per compatibilità globale
  const sendOrderToPrinter_table = (tableId, sconto) => {
    sendOrderToPrinter('orderPrinter_table', { tableId }, sconto, 'PRINT_ORDER_TABLE');
  };

  const sendOrderToPrinter_R_C = (deliveryId, sconto) => {
    sendOrderToPrinter('orderPrinter_R_C', { deliveryId }, sconto, 'PRINT_ORDER_R_C');
  };

  const dash_sendOrderToPrinter_table = (tableId, sconto, filter) => {
    sendOrderToPrinter('dash_orderPrinter_table', { tableId }, sconto, 'PRINT_ORDER_TABLE', filter);
  };

  const dash_sendOrderToPrinter_R_C = (deliveryId, sconto, filter) => {
    sendOrderToPrinter('dash_orderPrinter_R_C', { deliveryId }, sconto, 'PRINT_ORDER_R_C', filter);
  };

  return (
    <PrinterContext.Provider
      value={{
        connectionStatus,
        sendOrderToPrinter_table,
        sendOrderToPrinter_R_C,
        dash_sendOrderToPrinter_table,
        dash_sendOrderToPrinter_R_C,
        printCurrentCartOnly, // Esposta per useReservation
        errorOccurred,
        isDisablePrinter,
        socket,
        socketError
      }}
    >
      {children}
    </PrinterContext.Provider>
  );
};